import React, { useCallback } from 'react';
import { Popover } from 'antd';
import { Video, VideoContent, VideoContentSentiment } from '@/interfaces/video';
import Highlighter from 'react-highlight-words';

import styles from './styles.module.css';

interface IProps {
	video: Video;
	timeShow: boolean;
	searchTerm: string;
	entities: string[];
}

const durationInSeconds = (duration: string): number => {
	// If duration is just a number (seconds), return it directly
	const totalSeconds = parseInt(duration);
	if (!isNaN(totalSeconds)) {
		return totalSeconds;
	}
	
	// If duration is in "X hours, Y minutes, Z seconds" format, parse it
	return duration.split(', ').reduce((prev, curr, i) => {
		if (i === 0) {
			prev += +curr.split(' ')[0] * 3600;
		}
		if (i === 1) {
			prev += +curr.split(' ')[0] * 60;
		}
		if (i === 2) {
			prev += +curr.split(' ')[0];
		}
		return prev;
	}, 0);
};

const sentimentColor = (s: VideoContentSentiment): string => {
	if (s.pos > s.neg && s.pos > 0.1) return 'green';
	if (s.neg > s.pos && s.neg > 0.1) return 'red';
	if (s.pos > 0.1) return 'green';
	if (s.neg > 0.1) return 'red';
	return 'blue';
};

const last = [200, 161, 160, 121, 120, 81, 80, 41, 40, 1];

const columns = 40;
const rows = 5;
const amount = columns * rows;

export const toMMSS = (sec_num: number): string => {
	let hours = Math.floor(sec_num / 3600);
	let minutes = Math.floor((sec_num - hours * 3600) / 60);
	let seconds = sec_num - hours * 3600 - minutes * 60;

	if (hours < 10) {
		hours = 0 + hours;
	}
	if (minutes < 10) {
		minutes = 0 + minutes;
	}
	if (seconds < 10) {
		seconds = 0 + seconds;
	}

	if (hours >= 1) {
		minutes += 60;
	}

	return `${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
};

const RenderComponent = ({ 
	video, 
	searchTerm, 
	timeShow,
	entities
}: { 
	video: Video; 
	searchTerm: string; 
	timeShow: boolean;
	entities: string[];
}) => {
	
	const duration = durationInSeconds(video.metaData.duration);
	const divider = duration / amount;

	const s: { [key: number]: VideoContent } = useCallback(
		video.content?.reduce((p, c) => {
			const gridIndex = Math.round(c.start / divider);
			p[gridIndex] = c;
			return p;
		}, {} as any),
		[video, searchTerm]
	);

	return (
		<>
			{[...Array(columns * rows).keys()].map((i) => {
				if (s && s[i]) {
					return (
						<Popover
							key={s[i].text + s[i].id}
							content={
								<div>
									<Highlighter
										highlightStyle={{ backgroundColor: '#ffec3d' }}
										highlightClassName="YourHighlightClass"
										searchWords={entities.length > 0 ? entities : [searchTerm]}
										autoEscape={true}
										textToHighlight={`${toMMSS(Math.round(s[i].start))} - ${s[i].text}`}
									/>
								</div>
							}
							title=""
						>
							<div
								key={i}
								className={`${styles.item} ${styles.active} ${styles[sentimentColor(s[i].sentiment)]}`}
								onClick={() => {
									window.open(`${video.metaData.url}&t=${Math.round(s[i].start - 2)}s`);
								}}
							>
								{timeShow && last.includes(i + 1) && (
									<div className={`${timeShow ? styles.time : ''} ${i % 2 === 0 ? styles.timeright : styles.timeleft}`}>
										{i % 2 === 0
											? toMMSS(Math.round((i / amount) * duration))
											: toMMSS(Math.round(((i + 1) / amount) * duration))}
									</div>
								)}
							</div>
						</Popover>
					);
				}
				return (
					<div key={i} className={styles.item}>
						{timeShow && last.includes(i + 1) && (
							<div className={`${timeShow ? styles.time : ''} ${i % 2 === 0 ? styles.timeright : styles.timeleft}`}>
								{i % 2 === 0
									? toMMSS(Math.round((i / amount) * duration))
									: toMMSS(Math.round(((i + 1) / amount) * duration))}
							</div>
						)}
					</div>
				);
			})}
		</>
	);
};

const WaffleChart: React.FC<IProps> = ({ video, timeShow, searchTerm, entities }) => {
	return (
		<div
			className={`${styles.waffle} ${timeShow ? styles.margin : ''}`}
			style={{
				gridTemplateColumns: `repeat(${columns}, 1fr)`,
				gridTemplateRows: `repeat(${rows}, 1fr)`,
			}}
		>
			<RenderComponent video={video} searchTerm={searchTerm} timeShow={timeShow} entities={entities} />
		</div>
	);
};

export default WaffleChart;
